<template>
  <div>
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Donor/Org Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone Number
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Facility Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preferred Date
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preferred Time
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Number of Donors
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="8" class="px-6 py-6 text-center text-sm text-gray-500">
                Loading donations...
              </td>
            </tr>
            <tr v-else-if="filteredDonations.length === 0">
              <td colspan="8" class="px-6 py-6 text-center text-sm text-gray-500">
                No donations found.
              </td>
            </tr>
            <tr
              v-for="donation in filteredDonations"
              :key="donation.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.donorName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.phoneNumber }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.facilityName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.preferredDate }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.preferredTime }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <a
                  v-if="donation.mapUrl"
                  :href="donation.mapUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Open Map
                </a>
                <span v-else class="text-gray-500">N/A</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.numberOfDonors > 0 ? donation.numberOfDonors : 'N/A' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button
                  v-if="donation.status !== 'successful'"
                  @click="markDonationSuccessful(donation.id)"
                  :disabled="updatingDonationId === donation.id"
                  class="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ updatingDonationId === donation.id ? 'Updating...' : 'Mark Donated' }}
                </button>
                <span
                  v-else
                  class="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800"
                >
                  Successful
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-center gap-2 py-4 border-t border-gray-200">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1 || loading"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === 1 || loading
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Previous
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === page
              ? 'bg-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages || loading"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === totalPages || loading
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { DonationsAPI } from '@/services/donations';
import { useToast } from 'vue-toastification';

export default {
  name: 'DonationsTab',
  setup() {
    const toast = useToast();
    return { toast };
  },
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      updatingDonationId: null,
      currentPage: 1,
      totalPages: 1,
      donations: []
    };
  },
  computed: {
    filteredDonations() {
      if (!this.searchQuery) {
        return this.donations;
      }

      const query = this.searchQuery.toLowerCase();
      return this.donations.filter((donation) =>
        donation.donorName.toLowerCase().includes(query) ||
        donation.facilityName.toLowerCase().includes(query) ||
        donation.phoneNumber.toLowerCase().includes(query)
      );
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }

      return pages;
    }
  },
  mounted() {
    this.fetchDonations();
  },
  methods: {
    async fetchDonations(page = 1) {
      this.loading = true;
      try {
        const response = await DonationsAPI.getDonationBookings({ page, per_page: 20 });
        const payload = response?.data || {};
        const donationRows = payload.data || [];
        const meta = payload.meta || {};

        this.donations = donationRows.map((item) => {
          const coordinates = this.parsePointCoordinates(item.location);
          const mapUrl = coordinates
            ? `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`
            : '';

          return {
            id: item.donation_booking_id,
            donorName: [item.user?.first_name, item.user?.last_name].filter(Boolean).join(' ') || 'Unknown',
            phoneNumber: item.user?.phone_number || 'N/A',
            facilityName: item.facility_name || item.facility_code || 'N/A',
            preferredDate: this.formatDate(item.preferred_date),
            preferredTime: item.preferred_time || 'N/A',
            numberOfDonors: item.number_of_donors ?? 0,
            status: item.status || 'pending',
            mapUrl
          };
        });

        this.currentPage = meta.current_page || page;
        this.totalPages = meta.total_pages || 1;
        this.$emit('update-stats', {
          totalDonations: meta.total_count || donationRows.length || 0
        });
      } catch (error) {
        console.error('Failed to fetch donations:', error);
        this.donations = [];
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage || this.loading) {
        return;
      }
      this.fetchDonations(page);
    },
    async markDonationSuccessful(donationId) {
      this.updatingDonationId = donationId;
      try {
        await DonationsAPI.updateDonationStatus(donationId, { status: 'successful' });
        this.toast.success('Donation marked as successful.');
        await this.fetchDonations(this.currentPage);
      } catch (error) {
        console.error('Failed to update donation status:', error);
        const message = error.response?.data?.message || error.response?.data?.error || 'Failed to update donation status.';
        this.toast.error(message);
      } finally {
        this.updatingDonationId = null;
      }
    },
    parsePointCoordinates(pointValue) {
      if (!pointValue || typeof pointValue !== 'string') {
        return null;
      }

      const match = pointValue.match(/^POINT\(([-\d.]+)\s([-\d.]+)\)$/);
      if (!match) {
        return null;
      }

      return {
        lng: Number(match[1]),
        lat: Number(match[2])
      };
    },
    formatDate(dateString) {
      if (!dateString) {
        return 'N/A';
      }
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString();
    }
  }
};
</script>
